export type SectionKey =
	| 'overview'
	| '605'
	| '606'
	| '607'
	| '608'
	| '609'
	| '610'
	| '611'
	| '612'

export type SectionReference = {
	label: string
	to: string
}

export type SectionContent = {
	id: SectionKey
	title: string
	description: string
	obligations: string[]
	evidence: string[]
	intakeSections: Array<{ label: string; to: string }>
	templates: Array<{ label: string; filename: string }>
	consequences: string[]
	references: SectionReference[]
}

const FDA_MOCRA_OVERVIEW =
	'https://www.fda.gov/cosmetics/modernization-cosmetics-regulation-act-2022-mocra'
const MOCRA_STATUTE =
	'https://www.congress.gov/117/bills/hr2617/BILLS-117hr2617enr.pdf'
const FDA_MOCRA_TIMELINE =
	'https://www.fda.gov/cosmetics/cosmetics-news-events/modernization-cosmetics-regulation-act-2022#mocra_timeline'

export const SECTIONS: Record<SectionKey, SectionContent> = {
	overview: {
		id: 'overview',
		title: 'MoCRA Compliance Overview',
		description:
			'MoCRA (Modernization of Cosmetics Regulation Act of 2022) is the first major expansion of FDA oversight for cosmetics, requiring brands to register facilities, list products, maintain safety substantiation, report serious adverse events, and stay inspection-ready.',
		obligations: [
			'Confirm the responsible person, covered facilities, and SKUs—including any small-business exemptions or carve-outs.',
			'Document the core MoCRA pillars: facility registration, product listing, safety substantiation, serious adverse event reporting, and compliant labeling.',
			'Align suppliers and contract manufacturers with quality agreements and GMP readiness ahead of FDA rulemaking.',
			'Publish complaint intake, escalation, and recall playbooks so teams can act within statutory windows.',
			'Track emerging FDA rulemaking (fragrance allergens, talc testing, PFAS reporting) and weave milestones into the compliance roadmap.',
		],
		evidence: [
			'MoCRA readiness roadmap that assigns owners, deadlines, and status checkpoints.',
			'Centralized library of SOPs, safety files, complaint logs, and training assets leadership can review on demand.',
			'Supplier questionnaires and agreements documenting allergen controls, talc validation, and PFAS disclosures.',
			'Training calendar and communication scripts covering adverse event intake, inspections, and recalls.',
		],
		intakeSections: [
			{ label: 'Get Started', to: '/mocra/get-started' },
			{ label: 'Company', to: '/mocra/company' },
			{ label: 'Adverse Event Ops', to: '/mocra/adverse-events' },
			{ label: 'Recall Preparedness', to: '/mocra/recall-preparedness' },
			{ label: 'Regulatory Monitoring', to: '/mocra/regulatory-monitoring' },
		],
		templates: [
			{
				label: 'Regulatory Monitoring & Change Control SOP',
				filename: '§605-612-regulatory-watch.md',
			},
			{
				label: 'Supplier Oversight SOP',
				filename: '§605-612-supplier-oversight.md',
			},
			{
				label: 'Training & Documentation SOP',
				filename: '§605-612-training-documentation.md',
			},
		],
		consequences: [
			'Fragmented ownership slows FDA responses and weakens retailer and investor confidence.',
			'Missing a unified program charter leads to inconsistent filings, opening the door to warning letters, suspended registrations, or recalls.',
			'Lack of supplier alignment exposes GMP gaps that derail launches during audits.',
		],
		references: [
			{
				label: 'FDA MoCRA Landing Page',
				to: FDA_MOCRA_OVERVIEW,
			},
			{
				label: 'MoCRA Statute (H.R. 2617)',
				to: MOCRA_STATUTE,
			},
			{
				label: 'FDA MoCRA Timeline',
				to: FDA_MOCRA_TIMELINE,
			},
		],
	},
	'605': {
		id: '605',
		title: '§605 Serious Adverse Event Reporting',
		description:
			'MoCRA (Modernization of Cosmetics Regulation Act) requires the responsible person to triage cosmetic complaints, decide whether cases are serious, and file reports with the FDA (Food and Drug Administration) within 15 business days.',
		obligations: [
			'Log every complaint from consumers, retailers, health-care providers, and social media.',
			'Escalate potential serious cases to a medical reviewer the day they arrive.',
			'Submit FDA Form 3500A (with a label image) for each confirmed serious adverse event within the 15-day window.',
			'Send follow-up reports when new medical information arrives within one year.',
		],
		evidence: [
			'Complaint intake log showing timestamps, severity, and actions.',
			'Escalation ladder that names medical reviewers and regulatory approvers.',
			'Copies of FDA submissions and acknowledgement emails.',
			'Retention proof that complaint files stay on record for at least 6 years (3 for small businesses).',
		],
		intakeSections: [
			{ label: 'Adverse Event Ops', to: '/mocra/adverse-events' },
			{ label: 'Primary Contacts', to: '/mocra/primary-contacts' },
			{ label: 'Records Access', to: '/mocra/records-access' },
		],
		templates: [
			{
				label: 'Serious Adverse Event SOP',
				filename: '§605-adverse-event.md',
			},
		],
		consequences: [
			'FDA warning letters for late or missing submissions.',
			'Civil penalties or injunctions if serious events stay unreported.',
			'Retailer penalties and loss of shelf space during investigations.',
		],
		references: [
			{
				label: '21 U.S.C. § 364a – Adverse events',
				to: 'https://www.law.cornell.edu/uscode/text/21/364a',
			},
			{
				label: 'FDA: How to Report a Cosmetic Product Complaint',
				to: 'https://www.fda.gov/cosmetics/cosmetics-compliance-enforcement/how-report-cosmetic-product-related-complaint',
			},
		],
	},
	'606': {
		id: '606',
		title: '§606 Good Manufacturing Practice Preparation',
		description:
			'§606 directs FDA to set mandatory cosmetic GMP expectations. Brands and contract manufacturers should prove they already operate with documented, repeatable quality controls.',
		obligations: [
			'Write and maintain procedures for sanitation, equipment care, deviations, and change control.',
			'Risk-rank and audit suppliers, contract manufacturers, and labs that touch the product.',
			'Train GMP roles, keep completion records, and store controlled documents in a governed system.',
			'Track FDA rulemaking and remediate gaps before inspections start.',
		],
		evidence: [
			'Facility certifications (ISO 22716, cGMP audits) and remediation logs.',
			'Supplier quality agreements and audit schedules.',
			'Training records with completion dates, curricula, and role coverage.',
			'Deviation/CAPA (corrective and preventive action) reports with closure proofs.',
		],
		intakeSections: [
			{ label: 'Facilities', to: '/mocra/facilities' },
			{ label: 'Suppliers & Testing', to: '/mocra/suppliers-testing' },
			{ label: 'Training & Docs', to: '/mocra/training-documentation' },
			{ label: 'Policies & References', to: '/mocra/policies-references' },
		],
		templates: [
			{
				label: 'GMP Preparation SOP',
				filename: '§606-gmp-prep.md',
			},
			{
				label: 'Training & Documentation SOP',
				filename: '§605-612-training-documentation.md',
			},
		],
		consequences: [
			'Form 483 observations and warning letters once FDA audits begin.',
			'Production or launch delays caused by unqualified suppliers.',
			'Retailer delisting when they see missing quality controls.',
		],
		references: [
			{
				label: '21 U.S.C. § 364b – Good manufacturing practice',
				to: 'https://www.law.cornell.edu/uscode/text/21/364b',
			},
			{
				label: 'FDA Listening Session on Cosmetic GMP',
				to: 'https://www.fda.gov/cosmetics/cosmetics-news-events/public-meeting-good-manufacturing-practices-cosmetic-products-06012023',
			},
			{
				label: 'Draft Guidance: Cosmetic Good Manufacturing Practices',
				to: 'https://www.fda.gov/regulatory-information/search-fda-guidance-documents/draft-guidance-industry-cosmetic-good-manufacturing-practices',
			},
		],
	},
	'607': {
		id: '607',
		title: '§607 Facility Registration & Product Listing',
		description:
			'Facilities that make cosmetics for the U.S. market must register with FDA, and the responsible person must list every marketed product and update the data each year.',
		obligations: [
			'Register each manufacturing and processing facility within 60 days of first sale and renew yearly.',
			'Track FDA Establishment Identifier (FEI) status for internal and contract sites.',
			'Submit product listings with label images, responsible person details, and formulation basics.',
			'Update listings within 60 days when formulas, labels, or distribution change.',
		],
		evidence: [
			'Facility roster with FEI status and contact information.',
			'Product master list showing categories, professional-use flags, and planned launches.',
			'Label governance records that prove change control before printing.',
			'Distribution data connecting lot codes to customers for traceability.',
		],
		intakeSections: [
			{ label: 'Company', to: '/mocra/company' },
			{ label: 'Products', to: '/mocra/products' },
			{ label: 'Facilities', to: '/mocra/facilities' },
			{ label: 'Label Governance', to: '/mocra/label-governance' },
		],
		templates: [
			{
				label: 'Facility Registration SOP',
				filename: '§607-facility-registration.md',
			},
			{
				label: 'Product Listing SOP',
				filename: '§607-product-listing.md',
			},
		],
		consequences: [
			'FDA can suspend a facility registration, blocking U.S. distribution.',
			'Imports can be detained if the site lacks a valid FEI number.',
			'Retailers may decline onboarding without proof of listings.',
		],
		references: [
			{
				label: '21 U.S.C. § 364c – Registration and product listing',
				to: 'https://www.law.cornell.edu/uscode/text/21/364c',
			},
			{
				label: 'FDA: Cosmetic Product Facility Registrations & Listings',
				to: 'https://www.fda.gov/cosmetics/registration-listing-cosmetic-product-facilities-and-products',
			},
			{
				label:
					'Federal Register: Guidance on Cosmetic Product Facility Registrations',
				to: 'https://www.federalregister.gov/documents/2023/11/01/2023-24182/guidance-for-industry-cosmetic-product-facility-registrations-and-product-listings-under-the-modernization',
			},
		],
	},
	'608': {
		id: '608',
		title: '§608 Safety Substantiation',
		description:
			'Each marketed cosmetic must have data showing it is safe for users when used as directed or under customary conditions.',
		obligations: [
			'Maintain ingredient dossiers with toxicology summaries, exposure calculations, and supporting studies.',
			'Document the tests run on finished products, such as preservative efficacy, stability, and micro.',
			'Track how supplier updates or new scientific findings trigger label or formulation changes.',
			'Store substantiation evidence in systems that can be shared quickly during inspections.',
		],
		evidence: [
			'Supplier attestations, specifications, and safety certificates.',
			'Laboratory reports covering micro limits, stability, and challenge studies.',
			'Risk assessments linking product categories to testing plans.',
			'Change-control log tying safety data updates to artwork or SOP revisions.',
		],
		intakeSections: [
			{ label: 'Suppliers & Testing', to: '/mocra/suppliers-testing' },
			{ label: 'Products', to: '/mocra/products' },
			{ label: 'Policies & References', to: '/mocra/policies-references' },
		],
		templates: [
			{
				label: 'Safety Substantiation SOP',
				filename: '§608-safety-substantiation.md',
			},
			{
				label: 'Supplier Oversight SOP',
				filename: '§605-612-supplier-oversight.md',
			},
		],
		consequences: [
			'FDA can deem a product adulterated and request a recall.',
			'Civil lawsuits from consumers who suffer reactions without proof of safety work.',
			'Retailer quality teams may pause shipments until evidence is supplied.',
		],
		references: [
			{
				label: '21 U.S.C. § 364d – Safety substantiation',
				to: 'https://www.law.cornell.edu/uscode/text/21/364d',
			},
			{
				label: 'FDA: Product Testing of Cosmetics',
				to: 'https://www.fda.gov/cosmetics/cosmetics-science-research/product-testing-cosmetics',
			},
		],
	},
	'609': {
		id: '609',
		title: '§609 Labeling and Responsible Person Details',
		description:
			'Cosmetic labels must show the responsible person’s name and contact in the United States and will soon need fragrance allergen disclosures once FDA finalizes the list.',
		obligations: [
			'Display the responsible person contact (address, phone, or electronic contact) on every label.',
			'Control artwork changes through a documented review and approval process.',
			'Track fragrance allergens and prepare to update labels once FDA publishes the list.',
			'Flag professional-use products and make sure labels carry required statements.',
		],
		evidence: [
			'Artwork masters with version history and sign-offs.',
			'Label review checklist proving regulatory, quality, and marketing approvals.',
			'Allergen tracking register tied to formulas and artwork updates.',
			'Claim substantiation files linked to marketing statements on-pack.',
		],
		intakeSections: [
			{ label: 'Label Governance', to: '/mocra/label-governance' },
			{ label: 'Products', to: '/mocra/products' },
			{ label: 'Policies & References', to: '/mocra/policies-references' },
		],
		templates: [
			{
				label: 'Label Governance SOP',
				filename: '§609-label-governance.md',
			},
		],
		consequences: [
			'Misbranding citations and product holds if labels omit required contact details.',
			'Costly rework when allergen disclosures or claims lack documentation.',
			'Retailer compliance penalties for inaccurate or outdated labels.',
		],
		references: [
			{
				label: '21 U.S.C. § 364e – Labeling',
				to: 'https://www.law.cornell.edu/uscode/text/21/364e',
			},
			{
				label: 'FDA Cosmetic Labeling Guide',
				to: 'https://www.fda.gov/cosmetics/cosmetics-labeling-regulations/cosmetic-labeling-guide',
			},
		],
	},
	'610': {
		id: '610',
		title: '§610 Records Access and Inspection Readiness',
		description:
			'The FDA can request cosmetic records related to safety, adverse events, or ingredient controls, and companies must provide them promptly in the format requested.',
		obligations: [
			'Map record repositories (digital and physical) and assign custodians.',
			'Define secure transfer methods for sharing confidential files with regulators.',
			'Rehearse record-access drills to prove you can deliver within the expected timeframe.',
			'Keep records for the retention period MoCRA specifies (at least 6 years in most cases).',
		],
		evidence: [
			'Record inventory showing location, owner, and access level.',
			'SOP describing how to deliver files securely (encrypted email, portal, on-site review).',
			'Drill log with dates, participants, and outcomes.',
			'Distribution datasets tying lots to customers for traceability.',
		],
		intakeSections: [
			{ label: 'Records Access', to: '/mocra/records-access' },
			{ label: 'Training & Docs', to: '/mocra/training-documentation' },
			{ label: 'Policies & References', to: '/mocra/policies-references' },
		],
		templates: [
			{
				label: 'Records Access SOP',
				filename: '§610-records-access.md',
			},
		],
		consequences: [
			'FDA can escalate to warning letters or mandatory inspections when records are delayed.',
			'Retailer audits may fail if teams cannot retrieve documents during the visit.',
			'Internal investigations stall without a defined hand-off process.',
		],
		references: [
			{
				label: '21 U.S.C. § 364f – Records',
				to: 'https://www.law.cornell.edu/uscode/text/21/364f',
			},
			{
				label: 'FDA MoCRA Overview – Records Access',
				to: FDA_MOCRA_OVERVIEW,
			},
		],
	},
	'611': {
		id: '611',
		title: '§611 Mandatory Recall Authority',
		description:
			'MoCRA empowers FDA to mandate recalls if voluntary action is not fast enough. Companies must show they can trace lots, notify stakeholders, and confirm removal from the market.',
		obligations: [
			'Maintain a written recall plan with roles, decision criteria, and notification scripts.',
			'Assemble an incident response team with clear escalation paths.',
			'Track distribution to lot level for every sales channel.',
			'Run mock recalls and document lessons learned.',
		],
		evidence: [
			'Recall playbook, contact rosters, and communication templates.',
			'Distribution exports that connect lots to customers or retail locations.',
			'Incident logs showing prior withdrawals or drills and the corrective actions taken.',
			'Supplier and 3PL agreements that describe recall responsibilities.',
		],
		intakeSections: [
			{ label: 'Recall Preparedness', to: '/mocra/recall-preparedness' },
			{ label: 'Records Access', to: '/mocra/records-access' },
			{ label: 'Suppliers & Testing', to: '/mocra/suppliers-testing' },
		],
		templates: [
			{
				label: 'Recall Management SOP',
				filename: '§611-recall-management.md',
			},
		],
		consequences: [
			'FDA can order a compulsory recall and publish consumer alerts.',
			'Retailers may impose chargebacks or remove the brand from planograms.',
			'Civil liability increases if the recall is slow or incomplete.',
		],
		references: [
			{
				label: '21 U.S.C. § 364g – Mandatory recall authority',
				to: 'https://www.law.cornell.edu/uscode/text/21/364g',
			},
			{
				label: 'FDA Recalls, Market Withdrawals & Safety Alerts',
				to: 'https://www.fda.gov/safety/recalls-market-withdrawals-safety-alerts',
			},
		],
	},
	'612': {
		id: '612',
		title: '§612 Fragrance Allergen Transparency',
		description:
			'§612 directs FDA to publish a fragrance allergen list and timeline. Companies must monitor the rule, map affected SKUs, and update labels and consumer communications.',
		obligations: [
			'Monitor FDA updates so you know when the allergen list and compliance date go live.',
			'Map allergens to formulas and identify the SKUs that will need updated artwork.',
			'Coordinate with suppliers to receive timely allergen disclosures.',
			'Train labeling, regulatory, and customer-service teams on the new statements.',
		],
		evidence: [
			'Ingredient-to-artwork matrix showing potential allergen disclosures.',
			'Supplier attestations or certificates that declare allergen content.',
			'Change-control entries documenting artwork updates and approvals.',
			'Training slides or FAQ for customer-facing teams.',
		],
		intakeSections: [
			{ label: 'Label Governance', to: '/mocra/label-governance' },
			{ label: 'Suppliers & Testing', to: '/mocra/suppliers-testing' },
			{ label: 'Regulatory Monitoring', to: '/mocra/regulatory-monitoring' },
		],
		templates: [
			{
				label: 'Regulatory Monitoring & Change Control SOP',
				filename: '§605-612-regulatory-watch.md',
			},
			{
				label: 'Label Governance SOP',
				filename: '§609-label-governance.md',
			},
		],
		consequences: [
			'Misbranding actions if allergens stay off labels past the FDA deadline.',
			'Retailer compliance holds until updated packaging ships.',
			'Customer complaints or litigation tied to undisclosed allergen exposure.',
		],
		references: [
			{
				label: '21 U.S.C. § 364e – Labeling (Fragrance allergens)',
				to: 'https://www.law.cornell.edu/uscode/text/21/364e',
			},
			{
				label:
					'Federal Register: Testing Methods for Talc-containing Cosmetics',
				to: 'https://www.federalregister.gov/documents/2024/12/27/2024-30544/testing-methods-for-detecting-and-identifying-asbestos-in-talc-containing-cosmetic-products',
			},
		],
	},
}

export const SECTION_LIST = [
	SECTIONS.overview,
	SECTIONS['605'],
	SECTIONS['606'],
	SECTIONS['607'],
	SECTIONS['608'],
	SECTIONS['609'],
	SECTIONS['610'],
	SECTIONS['611'],
	SECTIONS['612'],
] as const
